// A multi-select filter drop-down: one button per facet ("App: All") that opens a list
// of options with ticks. Rows of chips grow with the data — a panel with three apps and
// four teams is already a two-line bar — so a filter with many values is rendered as this
// instead, and the bar stays one line whatever the registry holds.
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
const CHEVRON_SVG = `<svg class="fsel__caret" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>`;
const TICK_SVG = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`;
/** What the button says: nothing selected = All, one = its label, more = the count. */
function summary(items) {
    const on = items.filter((i) => i.on);
    if (on.length === 0)
        return t('common.all');
    if (on.length === 1)
        return on[0].label;
    return t('common.n_selected', { n: String(on.length) });
}
/**
 * Markup for one drop-down. `role` is what the caller's click handler dispatches on.
 * `single` marks a facet that holds at most one value (Status: Online or Offline) —
 * {@link pickFilterSelect} then clears the siblings instead of adding to them.
 */
export function filterSelect(label, role, items, opts = {}) {
    if (!items.length)
        return '';
    return `
        <div class="fsel" data-role="${esc(role)}"${opts.single ? ' data-single="1"' : ''}>
            <button type="button" class="fsel__btn${items.some((i) => i.on) ? ' is-active' : ''}" aria-expanded="false">
                <span class="fsel__label">${esc(label)}</span>
                <span class="fsel__value">${esc(summary(items))}</span>
                ${CHEVRON_SVG}
            </button>
            <div class="fsel__menu" hidden>
                ${items.map((i) => `
                    <button type="button" class="fsel__opt${i.on ? ' is-on' : ''}" data-key="${esc(i.key)}">
                        <span class="fsel__tick">${TICK_SVG}</span>${esc(i.label)}
                    </button>`).join('')}
            </div>
        </div>`;
}
/** Repaint one drop-down's button after its selection changed. */
export function refreshFilterSelect(fsel) {
    const opts = [...fsel.querySelectorAll('.fsel__opt')];
    const items = opts.map((o) => ({ key: '', label: o.textContent?.trim() ?? '', on: o.classList.contains('is-on') }));
    fsel.querySelector('.fsel__value').textContent = summary(items);
    fsel.querySelector('.fsel__btn').classList.toggle('is-active', items.some((i) => i.on));
}
/**
 * Update the DOM for a clicked option and return whether it ended up selected, so the
 * caller only has to mirror that into its own state. A single-value facet also drops the
 * sibling picks and closes — there is nothing more to choose there.
 */
export function pickFilterSelect(opt) {
    const fsel = opt.closest('.fsel');
    const on = !opt.classList.contains('is-on');
    if (fsel.dataset['single']) {
        fsel.querySelectorAll('.fsel__opt').forEach((o) => o.classList.toggle('is-on', o === opt && on));
        fsel.querySelector('.fsel__menu').hidden = true;
        fsel.querySelector('.fsel__btn').setAttribute('aria-expanded', 'false');
    }
    else {
        opt.classList.toggle('is-on', on);
    }
    refreshFilterSelect(fsel);
    return on;
}
function closeAll(root, except) {
    root.querySelectorAll('.fsel').forEach((f) => {
        if (f === except)
            return;
        f.querySelector('.fsel__menu').hidden = true;
        f.querySelector('.fsel__btn').setAttribute('aria-expanded', 'false');
    });
}
/**
 * Wire opening/closing for every drop-down inside `root`. Call ONCE per container (the
 * bar is re-rendered into the same element on every list reload); option clicks stay the
 * caller's, since only it knows which selection set the key belongs to.
 */
export function wireFilterSelects(root) {
    root.addEventListener('click', (e) => {
        const btn = e.target.closest('.fsel__btn');
        if (!btn)
            return;
        const fsel = btn.closest('.fsel');
        const menu = fsel.querySelector('.fsel__menu');
        closeAll(root, fsel);
        menu.hidden = !menu.hidden;
        btn.setAttribute('aria-expanded', String(!menu.hidden));
    });
    document.addEventListener('click', (e) => {
        if (!root.contains(e.target))
            closeAll(root);
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape')
        closeAll(root); });
}
